import type { FindingDetail, EvidenceDetail } from '../../components/Inspector.tsx';
import { neutralPointer } from '../discovery/collectorLabel.ts';
import { distinctEvidence, findingTaskLane } from '../discovery/evidenceSummary.ts';
import type {
  Attempt,
  Evidence,
  Finding,
  FindingFilterCriteria,
  FindingsRouteSearch,
  FindingSortOption,
  SourceRecord,
  WorkspaceFindingCounts,
} from './types.ts';

export interface FindingsCoverageSummary {
  kind: 'not-started' | 'active' | 'partial' | 'complete' | 'failed' | 'stopped';
  totalUnits: number;
  completedUnits: number;
  partialUnits: number;
  failedUnits: number;
  skippedUnits: number;
  pendingUnits: number;
}

/** Derive the findings page state from the attempt's real coverage records. */
export function selectFindingsCoverageSummary(
  attempt: Attempt | null,
  findingCount: number,
): FindingsCoverageSummary {
  if (!attempt) {
    return {
      kind: 'not-started',
      totalUnits: 0,
      completedUnits: 0,
      partialUnits: 0,
      failedUnits: 0,
      skippedUnits: 0,
      pendingUnits: 0,
    };
  }

  const coverage = attempt.coverage ?? [];
  const byStatus = {
    NOT_ATTEMPTED: 0,
    RUNNING: 0,
    PARTIAL: 0,
    COMPLETE: 0,
    FAILED: 0,
    SKIPPED: 0,
  };
  for (const unit of coverage) byStatus[unit.status] += 1;

  const hasRecordedCoverage = byStatus.COMPLETE > 0 || byStatus.PARTIAL > 0 || findingCount > 0;
  const active =
    attempt.status === 'QUEUED' ||
    attempt.status === 'RUNNING' ||
    attempt.status === 'STOP_REQUESTED';
  const incompleteCoverage = coverage.length > byStatus.COMPLETE;
  let kind: FindingsCoverageSummary['kind'];

  if (active) kind = 'active';
  else if (attempt.status === 'FAILED') kind = hasRecordedCoverage ? 'partial' : 'failed';
  else if (attempt.status === 'STOPPED') kind = hasRecordedCoverage ? 'partial' : 'stopped';
  else if (attempt.status === 'PARTIAL' || incompleteCoverage) kind = 'partial';
  else kind = 'complete';

  return {
    kind,
    totalUnits: coverage.length,
    completedUnits: byStatus.COMPLETE,
    partialUnits: byStatus.PARTIAL,
    failedUnits: byStatus.FAILED,
    skippedUnits: byStatus.SKIPPED,
    pendingUnits: byStatus.NOT_ATTEMPTED + byStatus.RUNNING,
  };
}

/** Keep list context when navigating to a read-only finding detail and back. */
export function preserveFindingsListSearch(
  search: FindingsRouteSearch,
  selected = search.selected,
): Record<string, string> {
  return {
    ...(search.q ? { q: search.q } : {}),
    ...(search.severity ? { severity: search.severity } : {}),
    ...(search.origin ? { origin: search.origin } : {}),
    ...(search.review ? { review: search.review } : {}),
    ...(search.remediation ? { remediation: search.remediation } : {}),
    ...(search.retest ? { retest: search.retest } : {}),
    ...(search.asset ? { asset: search.asset } : {}),
    ...(search.lane ? { lane: search.lane } : {}),
    ...(search.sort ? { sort: search.sort } : {}),
    ...(selected ? { selected } : {}),
  };
}

export const SEVERITIES: readonly Finding['normalizedSeverity'][] = [
  'CRITICAL',
  'HIGH',
  'MEDIUM',
  'LOW',
  'INFORMATIONAL',
  'UNKNOWN',
];
/** Severities a finding can carry. A judge promotes an observation to a finding and assigns its
 * severity, so INFORMATIONAL is a valid finding severity (a judged, security-relevant observation),
 * not merely an un-promoted record. */
export const FINDING_SEVERITIES = SEVERITIES;
export const CLAIM_ORIGINS: readonly Finding['claimOrigin'][] = [
  'SOURCE_REPORTED',
  'ANALYST_AUTHORED',
  'MODEL_INFERRED',
];
export const REVIEW_STATES: readonly Finding['analystReviewState'][] = [
  'UNREVIEWED',
  'NEEDS_EVIDENCE',
  'ANALYST_VERIFIED',
  'ANALYST_REJECTED',
];
export const REMEDIATION_STATES: readonly Finding['remediationState'][] = [
  'NOT_STARTED',
  'IN_PROGRESS',
  'AWAITING_RETEST',
  'ACCEPTED_RISK',
  'COMPLETED',
];
export const RETEST_OUTCOMES: readonly Finding['retestOutcome'][] = [
  'NOT_RECORDED',
  'FIXED',
  'STILL_VULNERABLE',
  'INCONCLUSIVE',
  'UNABLE_TO_TEST',
];

export const SEVERITY_RANK: Record<Finding['normalizedSeverity'], number> = {
  CRITICAL: 5,
  HIGH: 4,
  MEDIUM: 3,
  LOW: 2,
  INFORMATIONAL: 1,
  UNKNOWN: 0,
};

function zeroCounts<T extends string>(values: readonly T[]): Record<T, number> {
  return Object.fromEntries(values.map((value) => [value, 0])) as Record<T, number>;
}

export function selectWorkspaceFindingCounts(findings: readonly Finding[]): WorkspaceFindingCounts {
  const counts: WorkspaceFindingCounts = {
    total: findings.length,
    severity: zeroCounts(SEVERITIES),
    origin: zeroCounts(CLAIM_ORIGINS),
    review: zeroCounts(REVIEW_STATES),
    remediation: zeroCounts(REMEDIATION_STATES),
    retest: zeroCounts(RETEST_OUTCOMES),
  };
  for (const finding of findings) {
    counts.severity[finding.normalizedSeverity] += 1;
    counts.origin[finding.claimOrigin] += 1;
    counts.review[finding.analystReviewState] += 1;
    counts.remediation[finding.remediationState] += 1;
    counts.retest[finding.retestOutcome] += 1;
  }
  return counts;
}

export function groupFindingsByAsset(findings: readonly Finding[]): Map<string, Finding[]> {
  const groups = new Map<string, Finding[]>();
  for (const finding of findings) {
    for (const asset of finding.affectedAssets) {
      const group = groups.get(asset) ?? [];
      group.push(finding);
      groups.set(asset, group);
    }
  }
  return groups;
}

export function selectFinding(findings: readonly Finding[], findingId: string): Finding | null {
  return findings.find((finding) => finding.findingId === findingId) ?? null;
}

export function filterFindings(
  findings: readonly Finding[],
  filters: FindingFilterCriteria,
  evidence: readonly { evidenceId: string; kind: string }[] = [],
): Finding[] {
  const query = filters.query?.trim().toLowerCase();

  return findings.filter((finding) => {
    if (query) {
      const matchTitle = finding.title.toLowerCase().includes(query);
      const matchDesc = finding.description.toLowerCase().includes(query);
      const matchId = finding.findingId.toLowerCase().includes(query);
      const matchAsset = finding.affectedAssets.some((asset) =>
        asset.toLowerCase().includes(query),
      );
      if (!matchTitle && !matchDesc && !matchId && !matchAsset) {
        return false;
      }
    }

    if (filters.severity && filters.severity !== 'ALL') {
      if (finding.normalizedSeverity !== filters.severity) return false;
    }

    if (filters.origin && filters.origin !== 'ALL') {
      if (finding.claimOrigin !== filters.origin) return false;
    }

    if (filters.review && filters.review !== 'ALL') {
      if (finding.analystReviewState !== filters.review) return false;
    }

    if (filters.remediation && filters.remediation !== 'ALL') {
      if (finding.remediationState !== filters.remediation) return false;
    }

    if (filters.retest && filters.retest !== 'ALL') {
      if (finding.retestOutcome !== filters.retest) return false;
    }

    if (filters.asset && filters.asset !== 'ALL') {
      if (!finding.affectedAssets.includes(filters.asset)) return false;
    }

    if (filters.lane && filters.lane !== 'ALL') {
      if (findingTaskLane(finding, evidence) !== filters.lane) return false;
    }

    return true;
  });
}

export function sortFindings(
  findings: readonly Finding[],
  sortOption: FindingSortOption = 'severity-desc',
): Finding[] {
  const copy = [...findings];
  return copy.sort((a, b) => {
    switch (sortOption) {
      case 'severity-desc': {
        const diff = SEVERITY_RANK[b.normalizedSeverity] - SEVERITY_RANK[a.normalizedSeverity];
        if (diff !== 0) return diff;
        return (b.lastObservedAt || '').localeCompare(a.lastObservedAt || '');
      }
      case 'severity-asc': {
        const diff = SEVERITY_RANK[a.normalizedSeverity] - SEVERITY_RANK[b.normalizedSeverity];
        if (diff !== 0) return diff;
        return (a.lastObservedAt || '').localeCompare(b.lastObservedAt || '');
      }
      case 'date-desc':
        return (b.lastObservedAt || '').localeCompare(a.lastObservedAt || '');
      case 'date-asc':
        return (a.lastObservedAt || '').localeCompare(b.lastObservedAt || '');
      case 'title-asc':
        return a.title.localeCompare(b.title);
      case 'title-desc':
        return b.title.localeCompare(a.title);
      default:
        return 0;
    }
  });
}

export function filterAndSortFindings(
  findings: readonly Finding[],
  filters: FindingFilterCriteria,
  sortOption: FindingSortOption = 'severity-desc',
  evidence: readonly { evidenceId: string; kind: string }[] = [],
): Finding[] {
  const filtered = filterFindings(findings, filters, evidence);
  return sortFindings(filtered, sortOption);
}

export function findingToInspectorDetail(finding: Finding): FindingDetail {
  return {
    id: finding.findingId,
    title: finding.title,
    severity: finding.normalizedSeverity,
    origin: finding.claimOrigin,
    review: finding.analystReviewState,
    remediation: finding.remediationState,
    retest: finding.retestOutcome,
    affectedAssets: [...finding.affectedAssets],
    description: finding.description,
    evidenceIds: [...finding.evidenceIds],
  };
}

export function selectEvidenceForFinding(
  evidenceList: readonly Evidence[],
  finding: Finding,
): Evidence[] {
  const idSet = new Set(finding.evidenceIds);
  return distinctEvidence(evidenceList.filter((ev) => idSet.has(ev.evidenceId)));
}

export function selectSourceRecordsForEvidence(
  sources: readonly SourceRecord[],
  evidenceList: readonly Evidence[],
): SourceRecord[] {
  const idSet = new Set(evidenceList.map((ev) => ev.sourceRecordId));
  return sources.filter((src) => idSet.has(src.sourceRecordId));
}

export interface WorkspaceEvidenceCounts {
  total: number;
  rawToolOutputs: number;
  aiInterpretations: number;
  dnsRecords: number;
  networkEndpoints: number;
  errorRecords: number;
  sourceRecords: number;
}

export interface EvidenceFilterCriteria {
  query?: string;
  kind?: string | 'ALL';
  sourceClass?: 'RAW_TOOL_OUTPUT' | 'MODEL_ASSISTED' | 'ALL';
  findingId?: string | 'ALL';
}

export function selectWorkspaceEvidenceCounts(
  evidenceList: readonly Evidence[],
  sourceRecords: readonly SourceRecord[],
  findings?: readonly Finding[],
): WorkspaceEvidenceCounts {
  let rawCount = 0;
  let aiCount = 0;
  let dnsCount = 0;
  let endpointCount = 0;
  let errorCount = 0;

  for (const ev of evidenceList) {
    if (ev.kind === 'DNS_RECORD') dnsCount += 1;
    else if (ev.kind === 'NETWORK_ENDPOINT') endpointCount += 1;
    else if (ev.kind === 'ERROR_RECORD') errorCount += 1;

    const isModelInferred = findings?.some(
      (f) => f.evidenceIds.includes(ev.evidenceId) && f.claimOrigin === 'MODEL_INFERRED',
    );
    if (isModelInferred || ev.kind === 'ERROR_RECORD') {
      aiCount += 1;
    } else {
      rawCount += 1;
    }
  }

  const uniqueSourceIds = new Set(sourceRecords.map((sr) => sr.sourceRecordId));

  return {
    total: evidenceList.length,
    rawToolOutputs: rawCount,
    aiInterpretations: aiCount,
    dnsRecords: dnsCount,
    networkEndpoints: endpointCount,
    errorRecords: errorCount,
    sourceRecords: uniqueSourceIds.size,
  };
}

export function filterEvidence(
  evidenceList: readonly Evidence[],
  filters: EvidenceFilterCriteria,
  findings?: readonly Finding[],
  sourceRecords?: readonly SourceRecord[],
): Evidence[] {
  const query = filters.query?.trim().toLowerCase();
  const sourceRecordMap = new Map(sourceRecords?.map((sr) => [sr.sourceRecordId, sr]));

  return evidenceList.filter((ev) => {
    const sr = sourceRecordMap.get(ev.sourceRecordId);
    const citingFinding = findings?.find((f) => f.evidenceIds.includes(ev.evidenceId));
    const isAi = ev.kind === 'ERROR_RECORD' || citingFinding?.claimOrigin === 'MODEL_INFERRED';

    if (query) {
      const matchId = ev.evidenceId.toLowerCase().includes(query);
      const matchPointer = ev.sourcePointer.toLowerCase().includes(query);
      const matchSummary = (ev.summary ?? '').toLowerCase().includes(query);
      const matchKind = ev.kind.toLowerCase().includes(query);
      const matchSource = (sr?.sourceSystem ?? '').toLowerCase().includes(query);
      const matchFinding = citingFinding?.findingId.toLowerCase().includes(query);
      if (
        !matchId &&
        !matchPointer &&
        !matchSummary &&
        !matchKind &&
        !matchSource &&
        !matchFinding
      ) {
        return false;
      }
    }

    if (filters.kind && filters.kind !== 'ALL') {
      if (ev.kind !== filters.kind) return false;
    }

    if (filters.sourceClass && filters.sourceClass !== 'ALL') {
      if (filters.sourceClass === 'MODEL_ASSISTED' && !isAi) return false;
      if (filters.sourceClass === 'RAW_TOOL_OUTPUT' && isAi) return false;
    }

    if (filters.findingId && filters.findingId !== 'ALL') {
      const isCitedByFinding = findings?.some(
        (f) => f.findingId === filters.findingId && f.evidenceIds.includes(ev.evidenceId),
      );
      if (!isCitedByFinding) return false;
    }

    return true;
  });
}

export function evidenceToInspectorDetail(
  ev: Evidence,
  sourceRecord?: SourceRecord | null,
  citingFinding?: Finding | null,
  allFindings?: readonly Finding[],
): EvidenceDetail {
  const linkedFindings = allFindings
    ? allFindings.filter((f) => f.evidenceIds.includes(ev.evidenceId))
    : citingFinding
      ? [citingFinding]
      : [];

  const isAi =
    ev.kind === 'ERROR_RECORD' ||
    Boolean(citingFinding && citingFinding.claimOrigin === 'MODEL_INFERRED') ||
    linkedFindings.some((f) => f.claimOrigin === 'MODEL_INFERRED');

  const citingRefs = linkedFindings.map((f) => ({
    findingId: f.findingId,
    title: f.title,
    severity: f.normalizedSeverity,
  }));

  return {
    id: ev.evidenceId,
    kind: ev.kind,
    sourceClass: isAi ? 'MODEL_ASSISTED' : 'SOURCE_REPORTED',
    sourceSystem: sourceRecord?.sourceSystem ?? ev.sourcePointer,
    toolVersion: sourceRecord?.sourceVersion,
    capturedAt: ev.capturedAt,
    content:
      ev.summary ?? `Observation record for ${ev.kind} (${neutralPointer(ev.sourcePointer)})`,
    sourceRecordId: ev.sourceRecordId,
    sourcePointer: ev.sourcePointer,
    sha256: sourceRecord?.sha256,
    storageRef: sourceRecord?.storageRef,
    integrityState: 'UNVERIFIED',
    isAiInterpretation: isAi,
    aiReasoning: isAi
      ? 'Synthesized from underlying observation; generated claims remain unverified.'
      : undefined,
    citingFindings: citingRefs,
  };
}
